import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';

export interface GameUpdatePayload {
  fixture_id: number;
  status: string;
  home_score?: number;
  away_score?: number;
  elapsed?: number;
  league_api_id: number;
  event_type: 'game_start' | 'game_end' | 'half_time' | 'game_update';
  timestamp: string;
}

/**
 * 📡 Realtime Broadcast Listener (Fase 3)
 * 
 * Escuta eventos broadcast do live-sync-scheduler (V1):
 * - game_start: Jogo começou
 * - game_update: Score/status mudou
 * - half_time: Intervalo
 * - game_end: Jogo terminou
 * 
 * Substitui polling de 20-60s por updates instantâneos (~100-500ms)
 */
export function useRealtimeBroadcast() {
  const queryClient = useQueryClient();

  useEffect(() => {
    console.log('📡 Connecting to broadcast channel (game-updates)...');

    const channel = supabase
      .channel('game-updates')
      .on('broadcast', { event: 'game_update' }, (payload) => {
        const data = payload.payload as GameUpdatePayload;
        
        // Calcular latência do broadcast (apenas logging, sem invalidações duplicadas)
        const broadcastLatency = Date.now() - new Date(data.timestamp).getTime();
        console.log(`📡 Broadcast: ${data.event_type} | fixture: ${data.fixture_id} | latency: ${broadcastLatency}ms`);
        
        // NOTA: games e game-events são invalidados via postgres_changes (useRealtimeSync)
        // Broadcast serve apenas como backup e para game-statistics no game_end
        
        // Se jogo terminou, invalidar estatísticas (não coberto por postgres_changes)
        if (data.event_type === 'game_end') {
          queryClient.invalidateQueries({ queryKey: ['game-statistics', data.fixture_id] });
        }
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('✅ Broadcast channel subscribed');
        } else if (status === 'CLOSED') {
          console.warn('⚠️ Broadcast channel closed');
        } else if (status === 'CHANNEL_ERROR') {
          console.warn('⚠️ Broadcast channel error (will auto-reconnect)');
        }
      });

    return () => {
      console.log('📡 Disconnecting broadcast channel');
      supabase.removeChannel(channel);
    };
  }, [queryClient]);
}
